import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { usePlayer } from '../context/PlayerContext';
import { api } from '../lib/api';
import HeartButton from '../components/HeartButton';
import { Play, Shuffle, ChevronLeft, Heart, LogIn } from 'lucide-react';

const MeriPasand = () => {
  const { user, loading: authLoading, login } = useAuth();
  const { refresh } = useFavorites();
  const { playQueue, currentSong } = usePlayer();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get('/me/favorites').then((r) => {
      setSongs(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user]);

  if (authLoading) return <div className="min-h-screen"><Header /></div>;

  if (!user) {
    return (
      <div className="min-h-screen pb-32" data-testid="meri-pasand-page">
        <Header />
        <main className="max-w-md mx-auto px-6 pt-16 text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FF007F] to-[#FFB800] flex items-center justify-center mx-auto shadow-[0_0_40px_rgba(255,0,127,0.4)]">
            <Heart className="w-9 h-9 text-[#120505] fill-current" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#FFF3E0] mt-6">Meri Pasand</h1>
          <p className="text-base text-[#FFF3E0]/85 mt-4">
            Sign in to save songs to your private favourites — they'll follow you on every device.
          </p>
          <button
            data-testid="meri-pasand-signin-btn"
            onClick={login}
            className="mt-8 h-12 px-6 rounded-full bg-[#FFB800] text-[#120505] font-bold flex items-center gap-2 mx-auto hover:scale-[1.03] active:scale-[0.98] transition-transform shadow-[0_6px_20px_rgba(255,184,0,0.35)]"
          >
            <LogIn className="w-4 h-4" />
            Sign in with Google
          </button>
        </main>
      </div>
    );
  }

  const playAll = () => { if (songs.length) playQueue(songs, 0); };
  const playShuffled = () => {
    if (!songs.length) return;
    const idx = Math.floor(Math.random() * songs.length);
    playQueue(songs, idx);
  };

  return (
    <div className="min-h-screen pb-32" data-testid="meri-pasand-page">
      <Header />
      <main className="max-w-5xl mx-auto">
        <div className="relative h-56 sm:h-72 overflow-hidden bg-gradient-to-br from-[#FF007F]/50 via-[#3D1A1A] to-[#FFB800]/30">
          <div className="absolute inset-0 bg-[#120505]/40" />
          <Link
            to="/"
            data-testid="back-link"
            className="absolute top-4 left-4 w-11 h-11 rounded-full bg-[#120505]/70 backdrop-blur flex items-center justify-center hover:bg-[#220A0A]"
          >
            <ChevronLeft className="w-5 h-5 text-[#FFF3E0]" />
          </Link>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] mb-2">Niji</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#FFF3E0] leading-none flex items-center gap-3">
              Meri Pasand
              <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-[#FF007F] fill-current" />
            </h1>
            <p className="mt-3 text-sm text-[#FFF3E0]/85 max-w-lg font-body">
              Aapki apni playlist — {user.name}
            </p>
            <div className="mt-2 text-xs text-[#BFA5A5]">{songs.length} {songs.length === 1 ? 'song' : 'songs'}</div>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-4 flex gap-3 items-center">
          <button
            data-testid="mp-play-all-btn"
            onClick={playAll}
            disabled={!songs.length}
            className="h-12 px-6 rounded-full bg-[#FFB800] text-[#120505] font-bold flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 shadow-[0_6px_20px_rgba(255,184,0,0.3)]"
          >
            <Play className="w-4 h-4 fill-current" /> Play
          </button>
          <button
            data-testid="mp-shuffle-btn"
            onClick={playShuffled}
            disabled={!songs.length}
            className="h-12 w-12 rounded-full border border-[#3D1A1A] flex items-center justify-center hover:border-[#FF007F] transition-colors disabled:opacity-50"
            aria-label="Shuffle play"
          >
            <Shuffle className="w-4 h-4 text-[#FFF3E0]" />
          </button>
        </div>

        <div className="px-2 sm:px-4">
          {loading ? (
            <div className="text-center py-16 text-[#BFA5A5]">Loading your favourites…</div>
          ) : songs.length === 0 ? (
            <div className="text-center py-16 text-[#BFA5A5]">
              <Heart className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p>Abhi tak koi favourite nahi.</p>
              <p className="text-xs mt-2">Dil dabaiye kisi bhi gaane par, yahaan jama ho jaayega.</p>
            </div>
          ) : (
            <ul data-testid="mp-song-list">
              {songs.map((song, i) => (
                <li key={song.id}>
                  <div
                    data-testid={`mp-song-row-${i}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => playQueue(songs, i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        playQueue(songs, i);
                      }
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#220A0A] transition-colors text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 ${currentSong?.id === song.id ? 'bg-[#220A0A]' : ''}`}
                  >
                    <div className="w-8 text-center text-sm text-[#BFA5A5] font-body">{i + 1}</div>
                    <img src={song.thumbnail_url} alt="" className="w-12 h-12 rounded-md object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className={`truncate text-sm ${currentSong?.id === song.id ? 'text-[#FFB800]' : 'text-[#FFF3E0]'}`}>{song.title}</div>
                      <div className="text-xs text-[#BFA5A5] truncate">{song.artist}</div>
                    </div>
                    <HeartButton song={song} testId={`mp-heart-${i}`} onClick={() => setTimeout(refresh, 400)} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
};

export default MeriPasand;
